/**
 * Business-intent CTA copy and destinations — the single source for every
 * marketing surface that routes Business-intent organic traffic to the
 * Business walkthrough rather than the free funnel (conversion programme,
 * 31 Oct 2026).
 *
 * Why a walkthrough and not checkout: cold traffic sent straight to the
 * Business checkout has never converted. The Business conversion we want is a
 * walkthrough request, captured by the form at `/business#book-walkthrough`
 * (and `/business/ads#book-walkthrough` for paid landings).
 *
 * Prices derive from PRICING (lib/pricing-data.ts) — never hard-code a £
 * amount here or at a call site.
 */

import { PRICING } from "@/lib/pricing-data";

/** The organic Business walkthrough form anchor. */
export const BUSINESS_WALKTHROUGH_HREF = "/business#book-walkthrough" as const;

/** The paid-landing Business walkthrough form anchor (ads traffic only). */
export const BUSINESS_ADS_WALKTHROUGH_HREF =
  "/business/ads#book-walkthrough" as const;

/** The Business plan page. */
export const BUSINESS_PLAN_HREF = "/business" as const;

export const BUSINESS_WALKTHROUGH_CTA = "Request a Business walkthrough" as const;

export const BUSINESS_PRICING_CTA = "See Business pricing" as const;

/**
 * The "smaller firm" signpost shown under the Site Control walkthrough CTA on
 * the site-access / induction / sign-in / attendance compare pages, so a
 * builder-run firm that does not need per-site Site Control still has a route.
 */
export const BUSINESS_SMALLER_FIRM_LEAD = "Smaller firm?" as const;
export const BUSINESS_SMALLER_FIRM_LABEL =
  `Business is ${PRICING.business.display}/month with attendance and sign-in` as const;

/**
 * Marketing routes whose visitor intent is Business — the global mobile sticky
 * bar sends these to the Business walkthrough instead of the free funnel.
 *
 * Kept as plain slug lists (not derived from the compare / guide registries)
 * so the client sticky bar does not pull the whole compare dataset into every
 * marketing page's bundle. __tests__/marketing-sticky-mobile-cta-routing.test.ts
 * asserts these stay in lockstep with the `ctaVariant: "business-walkthrough"` compare
 * pages and the `intent: "business-walkthrough"` guides.
 */
export const BUSINESS_INTENT_COMPARE_SLUGS = [
  "best-construction-compliance-software",
  "best-construction-document-sharing-portal-uk",
  "best-construction-training-compliance-software-uk",
  "best-contractor-management-software-uk",
  "best-contractor-site-portal-software-uk",
  "best-cscs-card-verification-software-uk",
] as const;

export const BUSINESS_INTENT_GUIDE_SLUGS = [
  "health-and-safety-file-construction",
  "principal-contractor-duties",
  "review-subcontractor-rams",
] as const;

export const BUSINESS_INTENT_FEATURE_PATHS = [
  "/features/site-attendance",
  "/features/site-portal",
  "/features/site-files",
] as const;

const BUSINESS_INTENT_PATHS: ReadonlySet<string> = new Set([
  ...BUSINESS_INTENT_FEATURE_PATHS,
  ...BUSINESS_INTENT_COMPARE_SLUGS.map((slug) => `/compare/${slug}`),
  ...BUSINESS_INTENT_GUIDE_SLUGS.map((slug) => `/guides/${slug}`),
]);

/** True for a marketing route whose sticky CTA should be the Business walkthrough. */
export function isBusinessIntentPath(pathname: string): boolean {
  const normalised =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  return BUSINESS_INTENT_PATHS.has(normalised);
}
